import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ConversationList from './components/ConversationList';
import MessagePane from './components/MessagePane';
import './App.css';

const CONVERSATIONS_PER_PAGE = 20;
const API_URL = process.env.REACT_APP_API_URL;

function App() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalConversations, setTotalConversations] = useState(0);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);

  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);

  // Filter state
  const [filter, setFilter] = useState({ email: '', conversationId: '' });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const isFilterActive = filter.email.trim() !== '' || filter.conversationId.trim() !== '';

  const fetchConversations = useCallback(async (pageToFetch, filterOverride) => {
    const filterToUse = filterOverride || filter;
    if (pageToFetch === 1) {
        setLoadingConversations(true);
    } else {
        setLoadingMoreConversations(true);
    }
    setError(null);
    try {
      const params = new URLSearchParams({
        page: pageToFetch,
        limit: CONVERSATIONS_PER_PAGE,
      });
      if (filterToUse.email) params.append('email', filterToUse.email);
      if (filterToUse.conversationId) params.append('conversationId', filterToUse.conversationId);
      const response = await axios.get(`${API_URL}/api/conversations?${params.toString()}`);
      const { conversations: newConversations, totalPages: newTotalPages, totalConversations: newTotalConvos } = response.data;
      setConversations(prev => pageToFetch === 1 ? newConversations : [...prev, ...newConversations]);
      setTotalPages(newTotalPages);
      setTotalConversations(newTotalConvos);
      setCurrentPage(pageToFetch);
    } catch (err) {
      console.error("Error fetching conversations:", err);
      setError('Failed to load conversations.');
    } finally {
      if (pageToFetch === 1) {
        setLoadingConversations(false);
      } else {
        setLoadingMoreConversations(false);
      }
    }
  }, [filter]);

  useEffect(() => {
    fetchConversations(1);
  }, [fetchConversations]);

  useEffect(() => {
    if (selectedConversationId) {
      setLoadingMessages(true);
      setMessages([]);
      axios.get(`${API_URL}/api/conversations/${selectedConversationId}/messages`)
        .then(response => {
          setMessages(response.data);
          setLoadingMessages(false);
        })
        .catch(err => {
          console.error(`Error fetching messages for ${selectedConversationId}:`, err);
          setError('Failed to load messages.');
          setLoadingMessages(false);
        });
    }
  }, [selectedConversationId]);

  const handleSelectConversation = (convId) => {
    const conversation = conversations.find(c => c.conversationId === convId || c._id === convId);
    if (conversation) {
        const apiConversationId = conversation.conversationId || conversation._id;
        setSelectedConversationId(apiConversationId); 
    } else {
        console.warn("Selected conversation not found in the list, using provided ID anyway:", convId);
        setSelectedConversationId(convId); 
    }
  };

  const loadMoreConversations = () => {
    if (currentPage < totalPages && !loadingMoreConversations) {
      fetchConversations(currentPage + 1);
    }
  };

  // Filter modal logic
  const [filterForm, setFilterForm] = useState({ email: '', conversationId: '' });
  const openFilterModal = () => {
    setFilterForm(filter); // prefill with current filter
    setShowFilterModal(true);
  };
  const closeFilterModal = () => setShowFilterModal(false);
  const handleFilterChange = (e) => {
    setFilterForm({ ...filterForm, [e.target.name]: e.target.value });
  };
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setFilter({ ...filterForm });
    setShowFilterModal(false);
    fetchConversations(1, filterForm);
    setSelectedConversationId(null); // Optionally clear selection on filter
  };
  const handleClearFilter = () => {
    setFilter({ email: '', conversationId: '' });
    setFilterForm({ email: '', conversationId: '' });
    setShowFilterModal(false);
    fetchConversations(1, { email: '', conversationId: '' });
    setSelectedConversationId(null);
  };

  return (
    <div className="app-container">
      <div className="conversations-panel">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Conversations {totalConversations > 0 ? `(${totalConversations})` : ''}</h2>
          <button className="open-filter-btn" onClick={openFilterModal} title="Filter Conversations">Filter</button>
        </div>
        {isFilterActive && (
          <div className="active-filter-bar">
            <span>Filter: </span>
            {filter.email && <span>Email: <b>{filter.email}</b></span>}
            {filter.conversationId && <span>Conversation ID: <b>{filter.conversationId}</b></span>}
            <button className="clear-filter-btn" onClick={handleClearFilter} aria-label="Clear filter">&times;</button>
          </div>
        )}
        {showFilterModal && (
          <div className="filter-modal-overlay" onClick={closeFilterModal}>
            <div className="filter-modal-content" onClick={e => e.stopPropagation()}>
              <button className="filter-modal-close" onClick={closeFilterModal}>&times;</button>
              <h3>Filter Conversations</h3>
              <form onSubmit={handleFilterSubmit}>
                <div className="filter-form-group">
                  <label>Email:</label>
                  <input type="email" name="email" value={filterForm.email} onChange={handleFilterChange} placeholder="user@email.com" />
                </div>
                <div className="filter-form-group">
                  <label>Conversation ID:</label>
                  <input type="text" name="conversationId" value={filterForm.conversationId} onChange={handleFilterChange} placeholder="Conversation ID" />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button type="submit" className="apply-filter-btn">Apply</button>
                  <button type="button" className="clear-filter-btn" onClick={handleClearFilter}>Clear</button>
                  <button type="button" className="cancel-filter-btn" onClick={closeFilterModal}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {loadingConversations && currentPage === 1 && <p>Loading conversations...</p>}
        {error && !loadingConversations && <p className="error-message">{error}</p>}
        {!loadingConversations && !error && conversations.length === 0 && <p>No conversations yet.</p>}
        {conversations.length > 0 && (
          <ConversationList 
            conversations={conversations} 
            onSelectConversation={handleSelectConversation} 
            selectedConversationId={selectedConversationId} 
            onLoadMore={loadMoreConversations}
            hasMore={currentPage < totalPages}
            isLoadingMore={loadingMoreConversations}
          />
        )}
      </div>
      <div className="messages-panel">
        <h2>Messages</h2>
        {selectedConversationId ? (
          <MessagePane 
            messages={messages} 
            loading={loadingMessages} 
            error={error && selectedConversationId && !loadingMessages} 
          />
        ) : (
          <p>Select a conversation to see messages.</p>
        )}
      </div>
    </div>
  );
}

export default App; 